import { Component, Injector, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 * FranceEsgExteriorDescription Widget
 */
@Component({
  selector: 'france-esg-exterior-description-widget',
  templateUrl: './france-esg-exterior-description-widget.component.html',
  styleUrls: ['../../common/css/general.scss', '../../common/css/text-and-list.scss', './france-esg-exterior-description-widget.component.scss']
})
export class FranceEsgExteriorDescriptionWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Delete current row subject
   */
  private deleteCurrentRowSubject = new Subject<void>();

  /**
   * Delete current row observable
   */
  public deleteCurrentRow$ = this.deleteCurrentRowSubject.asObservable();

  /**
   * Add row subject
   */
  private addRowSubject = new Subject<any>();

  /**
   * Add row observable
   */
  public addRow$ = this.addRowSubject.asObservable();

  /**
   * Move current row up subject
   */
  private moveCurrentRowUpSubject = new Subject<void>();

  /**
   * Move current row up observable
   */
  public moveCurrentRowUp$ = this.moveCurrentRowUpSubject.asObservable();

  /**
   * Move current row down subject
   */
  private moveCurrentRowDownSubject = new Subject<void>();

  /**
   * Move current row down observable
   */
  public moveCurrentRowDown$ = this.moveCurrentRowDownSubject.asObservable();

  /**
   * Text & List section rows
   */
  rows: any = [];

  /**
   * Text & List section name
   */
  readonly textAndListSectionName: string;

  /**
  * Constructor
  */
  constructor(injector: Injector) {
    super(injector, 'franceEsgExteriorDescription', false);
    this.textAndListSectionName = `${this.name}`;//should be the same as defined in repository
  }

  /**
   * Widget's text area options (if used)
   */
  textAreaOptions: any = null;

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("franceEsgExteriorDescription", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    this.rows = this.getTextAndListSettingsOrSetDefault(this.textAndListSectionName, [
      {
        topicId: 1,
        fieldName: 'lettableArea',
        isCustom: true,
        customSelector: ".lettable-area-table",
        selected: true
      }, {
        topicId: 2,
        fieldName: 'storeysFloorsUndergroundLevels',
        isTextField: true,
        selected: true
      }, {
        topicId: 3,
        fieldName: 'yearBuilt',
        isTextField: true,
        selected: true
      }, {
        topicId: 4,
        fieldName: 'yearOfLastRefurbishment',
        isTextField: true,
        selected: true
      }, {
        topicId: 5,
        fieldName: 'buildingQualityAndConstruction',
        isTextField: true,
        selected: true
      }, {
        topicId: 6,
        fieldName: 'structureAndFoundations',
        isTextField: true,
        selected: true
      }, {
        topicId: 7,
        fieldName: 'windows',
        isTextField: true,
        selected: true
      }, {
        topicId: 8,
        fieldName: 'roof',
        isTextField: true,
        selected: true
      }, {
        topicId: 9,
        fieldName: 'facade',
        isTextField: true,
        selected: true
      }, {
        topicId: 10,
        fieldName: 'exteriorCondition',
        isTextField: true,
        selected: true
      }, {
        topicId: 11,
        fieldName: 'siteSurfacing',
        isTextField: true,
        selected: true
      }, {
        topicId: 12,
        fieldName: 'carParking',
        isTextField: true,
        selected: true
      }, {
        topicId: 13,
        fieldName: 'parkingRatio',
        isTextField: true,
        selected: true
      }, {
        topicId: 14,
        fieldName: 'yardAreas',
        isTextField: true,
        selected: true
      }, {
        topicId: 15,
        fieldName: 'landscaping',
        isTextField: true,
        selected: true
      }, {
        topicId: 16,
        fieldName: 'additionalComment',
        isTextField: true,
        selected: true
      }
    ]);

    this.loadReflist('ref_l3_esg_exterior_description_france_topics');

    this.mockupData$ = this.widgetService.loadMockup('franceEsgExteriorDescription').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('franceEsgExteriorDescription', this.getReport()).pipe();
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  preprocessLoadedData(data) {
    //sort lettablearea list by usage floor and tenant
    data["lettableArea"].sort((a, b) => (a.usage.localeCompare(b.usage) || a.floor.localeCompare(b.floor) || b.size - a.size  || a.tenant.localeCompare(b.tenant)));

    return data;
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);

    return this.widgetData;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Delete current row event handler
   */
  public deleteCurrentRow(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.deleteCurrentRowSubject.next();
  }

  /**
   * Add row event handler
   */
  public addRow(): void {
    const unselectedFields = this.rows.filter(item => !item.selected);
    if (unselectedFields.length > 0) {
      this.setTextAndListSettings(this.textAndListSectionName, this.rows);
      this.addRowSubject.next({
        topicId: this.getTopicId(),
        fieldName: null,
        isTextField: true,
        selected: true,
        lastTopicId: this.getTopicId()
      });
    }
  }

  /**
   * Move current row up event handler
   */
  public moveCurrentRowUp(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.moveCurrentRowUpSubject.next();
  }

  /**
   * Move current row down event handler
   */
  public moveCurrentRowDown(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.moveCurrentRowDownSubject.next();
  }

  /**
   * Get topic Id
   */
  private getTopicId() {
    const unselectedFields = this.rows.filter(item => !item.selected);
    return Math.min(...unselectedFields.map(item => item.topicId));
  }
}
